import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { NavController, Platform } from '@ionic/angular';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import {
  ActionPerformed as LocalActionPerformed,
  LocalNotifications,
} from '@capacitor/local-notifications';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { Badge } from '@capawesome/capacitor-badge/dist/esm';
import { ChatService } from './api/chat.service';
import { ChatMessagesService } from './chat-messages.service';
import { NotificationService } from './api/notification.service';
import { AppStorageService } from './app-storage.service';
import { StorageKeys } from './storage-keys';
import { TranslateService } from '@ngx-translate/core';
import { isNativePushPlatform } from './utils/runtime-config.util';

/**
 * Centralises all push-notification, FCM-channel and badge logic
 * that was previously inlined in AppComponent.
 */
@Injectable({ providedIn: 'root' })
export class AppPushNotificationService {
  /** Reference to a displayed alert so it can be dismissed on local-notif tap. */
  private alert: any;
  private isAppActive = true;
  private appStateListener: { remove?: () => Promise<void> | void } | null = null;

  constructor(
    private zone: NgZone,
    private router: Router,
    private navCtrl: NavController,
    private platform: Platform,
    private chatService: ChatService,
    private chatMessagesService: ChatMessagesService,
    private notificationService: NotificationService,
    private storage: AppStorageService,
    private translate: TranslateService,
  ) {}

  // ── Permission ───────────────────────────────────────────────────

  async requestPermission(): Promise<void> {
    if (!isNativePushPlatform(Capacitor.getPlatform())) {
      return;
    }

    const permCheck = await FirebaseMessaging.checkPermissions();

    if (permCheck.receive !== 'granted') {
      const permRequest = await FirebaseMessaging.requestPermissions();
      if (permRequest.receive === 'granted') {
        await this.setupPushPermission();
      }
    } else {
      await this.setupPushPermission();
    }
  }

  async setupPushPermission(): Promise<void> {
    if (!isNativePushPlatform(Capacitor.getPlatform())) {
      return;
    }

    await this.setupPush();

    const data = await this.storage.getAsync(StorageKeys.REMOVE_CHANNELS);
    await this.storage.setAsync(StorageKeys.REMOVE_CHANNELS, 'si');
    if (!data) {
      await this.removeChannels();
    }
    setTimeout(() => this.generateChannels(), 2000);
  }

  // ── FCM Channel management (Android) ────────────────────────────

  async removeChannels(): Promise<void> {
    try {
      const list: any = await FirebaseMessaging.listChannels();
      const channels = list.channels || [];
      for (const channel of channels) {
        await this.deleteChannel(channel);
      }
    } catch {
      // channel removal failed — non-fatal
    }
  }

  async generateChannels(): Promise<void> {
    if (!this.platform.is('desktop')) {
      await this.createNewChannels();
    }
  }

  async createNewChannels(): Promise<void> {
    try {
      const list: any = await FirebaseMessaging.listChannels();
      const channels = list.channels || [];
      for (const channel of channels) {
        if (channel.id !== 'general') {
          await this.deleteChannel(channel);
        }
      }
      await this.createChannelGeneral();
      await this.createChannelDefault();
    } catch {
      // channel sync failed — non-fatal
    }
  }

  async createChannelDefault(): Promise<void> {
    const dataChannel: any = {
      description: 'Notificaciones por defecto',
      id: 'default',
      name: 'Default',
      importance: 4,
      lightColor: '#000000',
      lights: true,
      visibility: 1,
      vibration: true,
      sound: 'default',
    };
    try {
      await FirebaseMessaging.createChannel(dataChannel);
    } catch {
      // channel creation failed — non-fatal
    }
  }

  async createChannelGeneral(): Promise<void> {
    const dataChannel: any = {
      description: 'Notificaciones generales',
      id: 'general',
      name: 'General',
      importance: 4,
      lightColor: '#FFFFFF',
      lights: true,
      visibility: 0,
      vibration: true,
      sound: 'default',
    };
    try {
      await FirebaseMessaging.createChannel(dataChannel);
    } catch {
      // channel creation failed — non-fatal
    }
  }

  async deleteChannel(data: any): Promise<void> {
    try {
      await FirebaseMessaging.deleteChannel(data);
    } catch {
      // channel deletion failed — non-fatal
    }
  }

  // ── Push listener registration ──────────────────────────────────

  setupPush(): void {
    if (!isNativePushPlatform(Capacitor.getPlatform())) {
      return;
    }

    FirebaseMessaging.removeAllListeners();
    void this.setupAppStateTracking();

    FirebaseMessaging.addListener(
      'notificationReceived',
      (notificacion: any) => {
        this.processReceivedNotification(notificacion.notification);
      }
    );

    FirebaseMessaging.addListener(
      'notificationActionPerformed',
      (notification: any) => {
        const notificationData = notification?.notification?.data || {};
        const notificationType = notificationData?.type;
        const notificationId = notificationData?.id;

        if (notificationType) {
          if (notificationType === '3') {
            this.navCtrl.navigateRoot('/app/notifications', { animated: true });
          }
          if (notificationType === '4' || notificationType === '6') {
            this.updateChatCount();
            const currentUrl = this.router.url || '';
            const target = `/app/messages/${notificationId}`;
            if (!currentUrl.startsWith(target)) {
              this.navCtrl.navigateRoot(target, { animated: true });
            }
          }
        }
      }
    );

    this.setupLocalPush();
  }

  private processReceivedNotification(notification: any): void {
    this.zone.run(() => {
      const notificationType = notification?.data?.type;
      const notificationId = notification?.data?.id;

      // Suprimir notificaciones de chat si ya estamos viendo esa conversación
      if ((notificationType === '4' || notificationType === '6') && notificationId) {
        const currentUrl = this.router.url || '';
        if (currentUrl.startsWith(`/app/messages/${notificationId}`)) {
          // Ya estamos en este chat: no mostrar notificación ni incrementar badge
          return;
        }
      }

      setTimeout(() => {
        const currentPlatform = Capacitor.getPlatform();
        if (currentPlatform === 'android' || currentPlatform === 'ios') {
          let { title, body, data } = notification;
          if ((!title || !body) && data) {
            title = title || data.title || data.subject || 'Foundek';
            body = body || data.body || data.message || data.alert || this.translate.instant('NOTIFICATIONS.NEW_NOTIFICATION');
          }
          title = title || 'Foundek';
          body = body || this.translate.instant('NOTIFICATIONS.NEW_NOTIFICATION');

          if (!this.isAppActive) {
            this.showLocalNotification(title, body, data);
          }
        }
      }, 2000);

      // Update counters based on notification type
      if (notificationType === '4' || notificationType === '6') {
        this.updateChatCount();
      }
      if (notificationType === '3') {
        this.updateNotificationCount();
      }
    });
  }

  private async setupAppStateTracking(): Promise<void> {
    if (this.appStateListener) {
      return;
    }

    try {
      const state = await App.getState();
      this.isAppActive = state?.isActive ?? true;
    } catch {
      this.isAppActive = true;
    }

    try {
      this.appStateListener = await App.addListener('appStateChange', ({ isActive }) => {
        this.isAppActive = isActive;
      });
    } catch {
      this.appStateListener = null;
    }
  }

  async setupLocalPush(): Promise<void> {
    await LocalNotifications.requestPermissions();
    LocalNotifications.addListener(
      'localNotificationActionPerformed',
      (notification: LocalActionPerformed) => {
        if (this.alert) this.alert.dismiss();

        const notificationData = notification?.notification?.extra?.data || {};
        const notificationType = notificationData?.type;
        const notificationId = notificationData?.id;

        if (notificationType) {
          if (notificationType === '3') {
            this.navCtrl.navigateRoot('/app/notifications', { animated: true });
          }
          if (notificationType === '4' || notificationType === '6') {
            this.updateChatCount();
            const currentUrl = this.router.url || '';
            const target = `/app/messages/${notificationId}`;
            const isInChatUI = currentUrl.startsWith('/app/chat') || currentUrl.startsWith('/app/messages');
            if (!isInChatUI || !currentUrl.startsWith(target)) {
              this.navCtrl.navigateRoot(target, { animated: true });
            }
          }
        }
      }
    );

    await Badge.requestPermissions();
    await Badge.clear();
    await this.setBadge(0);
  }

  // ── Counter updates ─────────────────────────────────────────────

  updateNotificationCount(): void {
    try {
      const token = this.storage.get(StorageKeys.TOKEN);
      const user = this.storage.getJSON(StorageKeys.USER) || {};
      if (!token || !user?.id) return;

      this.notificationService.getUnreadNotificationCount().subscribe({
        next: (count: number) => this.setBadge(count),
        error: () => {},
      });
    } catch {
      // ignore
    }
  }

  updateChatCount(): void {
    try {
      const token = this.storage.get(StorageKeys.TOKEN);
      const user = this.storage.getJSON(StorageKeys.USER) || {};
      if (!token || !user?.id) return;

      this.chatService.getChatCount().subscribe({
        next: (data: any) => {
          const count = data?.data || 0;
          this.chatMessagesService.updateUnreadMessages(count);
        },
        error: () => {},
      });
    } catch {
      // ignore
    }
  }

  // ── Local notification & badge ──────────────────────────────────

  async showLocalNotification(title: string, body: string, data: any): Promise<void> {
    const randomId = Math.floor(Math.random() * 10000) + 1;
    await LocalNotifications.schedule({
      notifications: [
        {
          title,
          body,
          id: randomId,
          extra: data,
          channelId: 'general',
        },
      ],
    });
  }

  async setBadge(count: number): Promise<void> {
    await Badge.set({ count });
  }
}
